import React, { useState, useEffect } from "react";
import Pagination from "@/components/pagination";
import Modal from "@/components/modal";
import SearchInput from "@/components/search-input";
import API from "@/services/api";
import toast from "react-hot-toast";
import { useComponentTranslation } from "@/services/i18n";

const translations = {
  en: {
    title: "Jobs",
    no_jobs: "No jobs found",
    job_updated: "Job updated",
    table: {
      title: "Title",
      organization: "Organization",
      city: "City",
      contract: "Contract",
      status: "Status",
      candidates: "Candidates",
      created_date: "Created Date",
    },
    status: {
      draft: "Draft",
      published: "Published",
      disabled: "Disabled",
      canceled: "Canceled",
    },
    modal: {
      title: "Job",
      information: "Information",
      raw: "Raw",
      fields: {
        title: "Title",
        status: "Status",
        organization: "Organization",
        city: "City",
        contract: "Contract",
        employment_type: "Employment Type",
        min_salary: "Min Salary",
        max_salary: "Max Salary",
        publication_date: "Publication Date",
        expiration_date: "Expiration Date",
        created_date: "Created Date",
        last_updated: "Last Updated",
        linkedin_rotation: "LinkedIn rotation",
      },
      contract_types: {
        CDI: "CDI",
        CDD: "CDD",
        INTERIM: "Temporary",
        FREELANCER: "Freelance",
        INTERN: "Internship",
        APPRENTICE: "Apprenticeship",
      },
      employment_types: {
        FULL_TIME: "Full-time",
        PART_TIME: "Part-time",
      },
      buttons: {
        cancel: "Cancel",
        save: "Save",
        saving: "Saving...",
      },
    },
  },
  fr: {
    title: "Emplois",
    no_jobs: "Aucun emploi trouvé",
    job_updated: "Emploi mis à jour",
    table: {
      title: "Titre",
      organization: "Organisation",
      city: "Ville",
      contract: "Contrat",
      status: "Statut",
      candidates: "Candidats",
      created_date: "Date de création",
    },
    status: {
      draft: "Brouillon",
      published: "Publié",
      disabled: "Désactivé",
      canceled: "Annulé",
    },
    modal: {
      title: "Emploi",
      information: "Informations",
      raw: "Brut",
      fields: {
        title: "Titre",
        status: "Statut",
        organization: "Organisation",
        city: "Ville",
        contract: "Contrat",
        employment_type: "Type d'emploi",
        min_salary: "Salaire minimum",
        max_salary: "Salaire maximum",
        publication_date: "Date de publication",
        expiration_date: "Date d'expiration",
        created_date: "Date de création",
        last_updated: "Dernière mise à jour",
        linkedin_rotation: "Rotation LinkedIn",
      },
      contract_types: {
        CDI: "CDI",
        CDD: "CDD",
        INTERIM: "Intérim",
        FREELANCER: "Freelance",
        INTERN: "Stage",
        APPRENTICE: "Apprentissage",
      },
      employment_types: {
        FULL_TIME: "Temps plein",
        PART_TIME: "Temps partiel",
      },
      buttons: {
        cancel: "Annuler",
        save: "Enregistrer",
        saving: "Enregistrement...",
      },
    },
  },
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedData, setSelectedData] = useState(null);
  const [selectedOrganization, setSelectedOrganization] = useState("");
  const limit = 25;
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    fetchJobs();
  }, [currentPage, searchTerm, selectedOrganization]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await API.post("/job/search", {
        page: currentPage,
        limit: limit,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedOrganization && { organization_id: selectedOrganization }),
      });

      if (response.ok) {
        setJobs(response.data || []);
        setTotal(response.total || 0);
      } else {
        toast.error(response.code || t("error_fetching_jobs"));
      }
    } catch (e) {
      console.error("Error fetching jobs:", e);
      toast.error(e.code || e.message || t("error_fetching_jobs"));
    } finally {
      setLoading(false);
    }
  };

  const handleViewRow = (data) => {
    setSelectedData(data);
  };

  const getStatusBadge = (status) => {
    if (!status) {
      return <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">-</span>;
    }

    const statusConfig = {
      DRAFT: { bg: "bg-gray-100", text: "text-gray-800", label: t("status.draft") },
      PUBLISHED: { bg: "bg-green-100", text: "text-green-800", label: t("status.published") },
      DISABLED: { bg: "bg-red-100", text: "text-red-800", label: t("status.disabled") },
      CANCELED: { bg: "bg-orange-100", text: "text-orange-800", label: t("status.canceled") },
    };

    const config = statusConfig[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
    return <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>;
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            className="w-64"
          />
          <OrganizationSelect
            value={selectedOrganization}
            onChange={(value) => {
              setSelectedOrganization(value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {/* Table Container */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">{t("no_jobs")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="text-left whitespace-nowrap text-xs font-medium text-gray-600 uppercase tracking-wider bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">{t("table.title")}</th>
                  <th className="px-4 py-3">{t("table.organization")}</th>
                  <th className="px-4 py-3">{t("table.city")}</th>
                  <th className="px-4 py-3">{t("table.contract")}</th>
                  <th className="px-4 py-3">{t("table.status")}</th>
                  <th className="px-4 py-3">{t("table.candidates")}</th>
                  <th className="px-4 py-3">{t("table.created_date")}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => {
                  const jobId = job._id || job.id;
                  return (
                    <tr key={jobId} onClick={() => handleViewRow(job)} className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">{job.title || "-"}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{job.organization_name || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{job.city || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{job.contract || "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap">{getStatusBadge(job.status)}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{job.total_candidates !== undefined ? job.total_candidates : "-"}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{job.created_at ? new Date(job.created_at).toLocaleDateString("fr-FR") : "-"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <div className="border-t border-gray-200 px-4">
          <Pagination total={total} limit={limit} currentPage={currentPage} onPageChange={setCurrentPage} />
        </div>
      </div>

      <ViewModal isOpen={!!selectedData} onClose={() => setSelectedData(null)} data={selectedData} fetchData={fetchJobs} translations={translations} />
    </div>
  );
};

const ViewModal = ({ isOpen, onClose, data, fetchData, translations }) => {
  const [activeTab, setActiveTab] = useState("view");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const { t } = useComponentTranslation(translations);

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const handleFormChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (!data) return;
    const jobId = data._id || data.id;
    if (!jobId) return;

    try {
      setLoading(true);
      const response = await API.put(`/job/${jobId}`, formData);

      if (response.ok) {
        toast.success(t("job_updated"));
        fetchData();
        onClose();
      } else {
        toast.error(response.code || t("error_updating_job"));
      }
    } catch (e) {
      console.error("Error updating job:", e);
      toast.error(e.code || e.message || t("error_updating_job"));
    } finally {
      setLoading(false);
    }
  };

  if (!data) return null;

  const jobId = data._id || data.id;

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-4xl w-full">
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">{t("modal.title")}</h2>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-4">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab("view")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "view" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("modal.information")}
            </button>
            <button
              onClick={() => setActiveTab("raw")}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === "raw" ? "border-purple-500 text-purple-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {t("modal.raw")}
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "raw" ? (
          <pre className="bg-gray-50 p-4 rounded-lg overflow-auto text-sm max-h-[70vh]">{JSON.stringify(data, null, 2)}</pre>
        ) : (
          <div className="">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.title")}</label>
                <input
                  type="text"
                  value={formData.title ?? ""}
                  onChange={(e) => handleFormChange("title", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.status")}</label>
                <select
                  value={formData.status ?? ""}
                  onChange={(e) => handleFormChange("status", e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  <option value="">-</option>
                  <option value="DRAFT">{t("status.draft")}</option>
                  <option value="PUBLISHED">{t("status.published")}</option>
                  <option value="DISABLED">{t("status.disabled")}</option>
                  <option value="CANCELED">{t("status.canceled")}</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.organization")}</label>
                <input
                  type="text"
                  value={formData.organization_name ?? ""}
                  onChange={(e) => handleFormChange("organization_name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  readOnly
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.city")}</label>
                <input
                  type="text"
                  value={formData.city ?? ""}
                  onChange={(e) => handleFormChange("city", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.contract")}</label>
                <select
                  value={formData.contract ?? ""}
                  onChange={(e) => handleFormChange("contract", e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  <option value="">-</option>
                  <option value="CDI">{t("modal.contract_types.CDI")}</option>
                  <option value="CDD">{t("modal.contract_types.CDD")}</option>
                  <option value="INTERIM">{t("modal.contract_types.INTERIM")}</option>
                  <option value="FREELANCER">{t("modal.contract_types.FREELANCER")}</option>
                  <option value="INTERN">{t("modal.contract_types.INTERN")}</option>
                  <option value="APPRENTICE">{t("modal.contract_types.APPRENTICE")}</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.employment_type")}</label>
                <select
                  value={formData.employment_type ?? ""}
                  onChange={(e) => handleFormChange("employment_type", e.target.value || null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  <option value="">-</option>
                  <option value="FULL_TIME">{t("modal.employment_types.FULL_TIME")}</option>
                  <option value="PART_TIME">{t("modal.employment_types.PART_TIME")}</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.min_salary")}</label>
                <input
                  type="number"
                  value={formData.remuneration_min ?? ""}
                  onChange={(e) => handleFormChange("remuneration_min", e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.max_salary")}</label>
                <input
                  type="number"
                  value={formData.remuneration_max ?? ""}
                  onChange={(e) => handleFormChange("remuneration_max", e.target.value ? parseFloat(e.target.value) : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.publication_date")}</label>
                <input
                  type="date"
                  value={formData.publication_date ? new Date(formData.publication_date).toISOString().split("T")[0] : ""}
                  onChange={(e) => handleFormChange("publication_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.expiration_date")}</label>
                <input
                  type="date"
                  value={formData.expiration_date ? new Date(formData.expiration_date).toISOString().split("T")[0] : ""}
                  onChange={(e) => handleFormChange("expiration_date", e.target.value ? new Date(e.target.value).toISOString() : null)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.created_date")}</label>
                <input
                  type="text"
                  value={formData.created_at ? new Date(formData.created_at).toLocaleString("fr-FR") : "-"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  readOnly
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t("modal.fields.last_updated")}</label>
                <input
                  type="text"
                  value={formData.updated_at ? new Date(formData.updated_at).toLocaleString("fr-FR") : "-"}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50"
                  readOnly
                />
              </div>
            </div>
            
            {/* LinkedIn rotation toggle */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.channel_linkedin ?? true}
                  onChange={(e) => handleFormChange("channel_linkedin", e.target.checked)}
                  className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <div>
                  <span className="text-sm font-medium text-gray-900">{t("modal.fields.linkedin_rotation")}</span>
                  <p className="text-xs text-gray-500">Include this job in the LinkedIn rotation</p>
                </div>
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                {t("modal.buttons.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t("modal.buttons.saving") : t("modal.buttons.save")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// Organization Select Component
const OrganizationSelect = ({ value, onChange }) => {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const response = await API.post("/organization/search", { approved: true });

      if (response.ok) {
        setOrganizations(response.data || []);
      } else {
        toast.error("Failed to load organizations");
      }
    } catch (e) {
      console.error("Error fetching organizations:", e);
      toast.error("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-w-[250px]"
        disabled={loading}
      >
        <option value="">All Organizations</option>
        {organizations.map((org) => (
          <option key={org._id} value={org._id}>
            {org.name}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  );
};

export default Jobs;
